import { db } from "../../../../../firebaseConfig";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  getDocFromServer,
  getDocFromCache,
  collection,
  query,
  where,
  getCountFromServer,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { retryOperation } from "./utils.js";
import { getDisplayName } from "../../../../../utils/userDisplay.js";
import { isLowQuotaMode } from "../../../../../firebase/firestoreFetchPolicy";
import { ensureFreshToken } from "../../../../../firebase/authUtils";

/**
 * Create a user document in Firestore
 * @param {Object} user - Firebase user object
 * @param {Object} additionalData - Additional user data
 */
export const createUserDocument = async (user, additionalData) => {
  if (!user) return;

  try {
    const userRef = doc(db, "users", user.uid);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      const { email } = user;
      const display =
        additionalData?.displayName ||
        additionalData?.name ||
        getDisplayName(additionalData) ||
        user.displayName ||
        "";

      const createdAt = serverTimestamp();

      try {
        await setDoc(userRef, {
          email,
          displayName: display,
          createdAt,
          ...additionalData,
        });
      } catch (error) {
        console.error("Error creating user document:", error);
        throw error;
      }
    }
  } catch (error) {
    console.error("Error checking user document:", error);
    throw error;
  }
};

/**
 * Get user data by UID
 * @param {string} uid - User ID
 * @param {Object} options - Options including forceCount
 * @returns {Promise<Object|null>} - User data or null
 */
export const getUserData = async (uid, options = {}) => {
  const { forceCount = false } = options;
  if (!uid) throw new Error("No user ID provided");

  return retryOperation(async () => {
    await ensureFreshToken();

    const userRef = doc(db, "users", uid);

    const forceNetwork =
      process.env.REACT_APP_FORCE_SERVER_USER_READS === "true";
    const envPolicy = process.env.REACT_APP_USER_FETCH_POLICY;
    const lowQuota = isLowQuotaMode();
    const fetchPolicy =
      envPolicy || (forceNetwork ? "network-only" : "cache-first");

    let userSnap;
    if (fetchPolicy === "network-only") {
      try {
        userSnap = await (typeof getDocFromServer === "function"
          ? getDocFromServer(userRef)
          : getDoc(userRef));
      } catch (e) {
        userSnap = await getDoc(userRef);
      }
    } else {
      try {
        const cacheSnap = await (typeof getDocFromCache === "function"
          ? getDocFromCache(userRef)
          : getDoc(userRef));
        if (cacheSnap?.exists()) {
          userSnap = cacheSnap;
        } else if (lowQuota) {
          userSnap = cacheSnap;
        } else {
          try {
            userSnap = await (typeof getDocFromServer === "function"
              ? getDocFromServer(userRef)
              : getDoc(userRef));
          } catch (e) {
            userSnap = await getDoc(userRef);
          }
        }
      } catch (e) {
        userSnap = await getDoc(userRef);
      }
    }

    if (userSnap.exists()) {
      const userData = userSnap.data();

      let postCount = 0;
      const disableCounts =
        process.env.REACT_APP_DISABLE_USER_POST_COUNTS === "true" &&
        !forceCount;
      if (!disableCounts) {
        try {
          const postsRef = collection(db, "posts");
          const q = query(
            postsRef,
            where("userId", "==", uid),
            where("status", "==", "active"),
          );
          const postCountSnapshot = await retryOperation(() =>
            getCountFromServer(q),
          );
          postCount = postCountSnapshot.data().count;
        } catch (countErr) {
          console.warn("Could not fetch post count:", countErr);
          postCount = userData.postCount || 0;
        }
      } else {
        postCount = userData.postCount || 0;
      }

      return {
        id: userSnap.id,
        ...userData,
        postCount,
      };
    }

    return null;
  });
};

/**
 * Get user posts by UID
 * @param {string} userId - User ID
 * @returns {Promise<Array<Object>>} - Array of post objects
 */
export const getUserPosts = async (userId) => {
  if (!userId) {
    console.warn("getUserPosts: No user ID provided. Returning empty array.");
    return [];
  }

  return retryOperation(async () => {
    const postsRef = collection(db, "posts");
    // Primary: new posts use userId (no status filter - filter client-side)
    const q1 = query(
      postsRef,
      where("userId", "==", userId),
      orderBy("timestamp", "desc"),
    );

    console.log(
      `getUserPosts: Attempting to fetch posts for userId: "${userId}" using field 'userId'`,
    );

    let posts = [];
    try {
      const snap1 = await getDocs(q1);
      snap1.forEach((d) => {
        const data = d.data();
        // Filter out deleted posts client-side
        if (data?.content && data.status !== "deleted") {
          posts.push({
            id: d.id,
            ...data,
            timestamp: data.timestamp?.toDate
              ? data.timestamp.toDate()
              : new Date(data.timestamp),
          });
        }
      });
    } catch (e) {
      console.warn(
        "getUserPosts: primary query failed, will attempt legacy fallback",
        e?.message || e,
      );
    }

    // Fallback: legacy posts used 'uid' (no status filter - filter client-side)
    if (posts.length === 0) {
      try {
        console.log(
          `getUserPosts: Falling back to legacy field 'uid' for userId: "${userId}"`,
        );
        const q2 = query(
          postsRef,
          where("uid", "==", userId),
          orderBy("timestamp", "desc"),
        );
        const snap2 = await getDocs(q2);
        snap2.forEach((d) => {
          const data = d.data();
          // Filter out deleted posts client-side
          if (data?.content && data.status !== "deleted") {
            posts.push({
              id: d.id,
              ...data,
              timestamp: data.timestamp?.toDate
                ? data.timestamp.toDate()
                : new Date(data.timestamp),
            });
          }
        });
      } catch (e2) {
        console.warn(
          "getUserPosts: legacy fallback query failed",
          e2?.message || e2,
        );
      }
    }

    console.log(
      `getUserPosts: Successfully fetched ${posts.length} posts for userId: "${userId}"`,
    );
    return posts;
  });
};